import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiError } from './api';

export type AdminRoleClient = 'owner' | 'manager' | 'analyst' | 'accountant' | 'viewer';

export interface AuthUser {
  userId: string;
  role: 'admin' | 'affiliate';
  email: string;
  name: string;
  adminRole?: AdminRoleClient;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    api<AuthUser>('/api/v1/auth/me', { withCredentials: true })
      .then(setUser)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          setUser(null);
        } else {
          setError(err);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  async function logout() {
    try {
      await api('/api/v1/auth/logout', { method: 'POST', withCredentials: true });
    } catch {
      // Even if the server call fails, we still want to clear local state.
    }
    setUser(null);
    navigate('/login', { replace: true });
  }

  const canWrite = user?.role === 'admin' && (user.adminRole === 'owner' || user.adminRole === 'manager');
  const canMarkPaid =
    user?.role === 'admin' &&
    (user.adminRole === 'owner' || user.adminRole === 'manager' || user.adminRole === 'accountant');

  return { user, loading, error, logout, canWrite, canMarkPaid };
}
